/**
 * BrainyTots Gift With Purchase (GWP) Engine
 * Auto-adds/removes free gift products based on cart spend thresholds.
 *
 * Reads config from #bt-gwp-config JSON block (rendered by bt-gwp-config.liquid).
 * Integrates with Dawn's pub/sub system for cart update coordination.
 */
(function () {
  'use strict';

  /* ========================================================================
     Config Loading
     ======================================================================== */

  function loadConfig() {
    var el = document.getElementById('bt-gwp-config');
    if (!el) {
      console.warn('[GWP] Config element #bt-gwp-config not found');
      return null;
    }

    var config;
    try {
      config = JSON.parse(el.textContent);
    } catch (e) {
      console.error('[GWP] Failed to parse config:', e);
      return null;
    }

    if (!config.enabled) {
      console.warn('[GWP] GWP is not enabled in config');
      return null;
    }
    if (!config.tiers || config.tiers.length === 0) {
      console.warn('[GWP] No tiers configured');
      return null;
    }

    // Filter out invalid tiers (threshold <= 0 or missing variantId)
    config.tiers = config.tiers.filter(function (tier) {
      return tier.threshold > 0 && tier.variantId;
    });

    if (config.tiers.length === 0) {
      console.warn('[GWP] No valid tiers after filtering');
      return null;
    }

    // Sort tiers by threshold descending (highest first)
    config.tiers.sort(function (a, b) {
      return b.threshold - a.threshold;
    });

    // Build a set of configured variant IDs for fast lookup
    config._variantIds = {};
    config.tiers.forEach(function (tier) {
      config._variantIds[String(tier.variantId)] = tier.id;
    });

    return config;
  }

  /* ========================================================================
     Cart API Helpers
     ======================================================================== */

  function getRoutePrefix() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function fetchCart() {
    return fetch(getRoutePrefix() + 'cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('[GWP] HTTP ' + res.status);
      return res.json();
    });
  }

  function addToCart(variantId, tierId) {
    return fetch(getRoutePrefix() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              _gift: 'true',
              _gift_tier: tierId,
            },
          },
        ],
      }),
    }).then(function (res) {
      if (!res.ok) throw new Error('[GWP] HTTP ' + res.status);
      return res.json();
    });
  }

  function removeFromCart(itemKey) {
    return fetch(getRoutePrefix() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0,
      }),
    }).then(function (res) {
      if (!res.ok) throw new Error('[GWP] HTTP ' + res.status);
      return res.json();
    });
  }

  function updateCartItem(itemKey, quantity) {
    return fetch(getRoutePrefix() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: quantity,
      }),
    }).then(function (res) {
      if (!res.ok) throw new Error('[GWP] HTTP ' + res.status);
      return res.json();
    });
  }

  /* ========================================================================
     Gift Identification
     ======================================================================== */

  function isGiftItem(item) {
    if (!item.properties || item.properties._gift !== 'true') return false;
    // Also verify variant ID matches a configured tier
    return String(item.variant_id) in config._variantIds;
  }

  function getGiftTierId(item) {
    return item.properties && item.properties._gift_tier
      ? item.properties._gift_tier
      : null;
  }

  /* ========================================================================
     Core Logic
     ======================================================================== */

  function getNonGiftTotal(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (!isGiftItem(item)) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  function getQualifyingTiers(nonGiftTotal) {
    var qualifying = [];
    for (var i = 0; i < config.tiers.length; i++) {
      if (nonGiftTotal >= config.tiers[i].threshold) {
        qualifying.push(config.tiers[i]);
        if (config.mode === 'replacement') {
          // In replacement mode, only the highest qualifying tier (first match since sorted desc)
          break;
        }
      }
    }
    return qualifying;
  }

  function getGiftState(cart) {
    var nonGiftTotal = getNonGiftTotal(cart);
    var qualifyingTiers = getQualifyingTiers(nonGiftTotal);
    var qualifyingIds = {};
    qualifyingTiers.forEach(function (tier) {
      qualifyingIds[tier.id] = tier;
    });

    // Current gift items in cart
    var giftsInCart = {};
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) {
        var tierId = getGiftTierId(item);
        if (tierId) {
          giftsInCart[tierId] = item;
        }
      }
    });

    // Determine what needs to change
    var toAdd = [];
    var toRemove = [];
    var toFix = [];

    // Gifts that should be added (qualifying but not in cart)
    qualifyingTiers.forEach(function (tier) {
      if (!giftsInCart[tier.id]) {
        toAdd.push(tier);
      }
    });

    // Gifts that should be removed (in cart but not qualifying)
    Object.keys(giftsInCart).forEach(function (tierId) {
      if (!qualifyingIds[tierId]) {
        toRemove.push(giftsInCart[tierId]);
      }
    });

    // Gifts with wrong quantity (should always be 1)
    Object.keys(giftsInCart).forEach(function (tierId) {
      var item = giftsInCart[tierId];
      if (qualifyingIds[tierId] && item.quantity !== 1) {
        toFix.push(item);
      }
    });

    // Next tier to unlock (lowest threshold tier not yet qualifying)
    var nextTier = null;
    // Tiers are sorted desc, walk from end (lowest) to find next unlock
    for (var i = config.tiers.length - 1; i >= 0; i--) {
      if (nonGiftTotal < config.tiers[i].threshold) {
        nextTier = config.tiers[i];
        break;
      }
    }

    // Progress percentage toward max threshold
    var maxThreshold = config.tiers[0].threshold; // highest (sorted desc)
    var progressPercent = maxThreshold > 0 ? Math.min((nonGiftTotal / maxThreshold) * 100, 100) : 0;

    var unlockedCount = qualifyingTiers.length;
    var allUnlocked = unlockedCount === config.tiers.length;

    return {
      nonGiftTotal: nonGiftTotal,
      qualifyingTiers: qualifyingTiers,
      toAdd: toAdd,
      toRemove: toRemove,
      toFix: toFix,
      nextTier: nextTier,
      progressPercent: progressPercent,
      allUnlocked: allUnlocked,
      unlockedCount: unlockedCount,
    };
  }

  /* ========================================================================
     Token Replacement & Formatting
     ======================================================================== */

  function formatMoney(paise) {
    var rupees = Math.round(paise) / 100;
    return rupees.toLocaleString('en-IN');
  }

  function replaceTokens(template, data) {
    if (!template) return '';
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return data[key] !== undefined ? data[key] : match;
    });
  }

  function buildTokenData(state) {
    var remaining = 0;
    if (state.nextTier) {
      remaining = state.nextTier.threshold - state.nonGiftTotal;
      if (remaining < 0) remaining = 0;
    }

    // Current qualifying tier (highest unlocked)
    var currentTier = state.qualifyingTiers.length > 0 ? state.qualifyingTiers[0] : null;

    return {
      remaining: formatMoney(remaining),
      gift_name: currentTier ? currentTier.name : '',
      next_gift_name: state.nextTier ? state.nextTier.name : '',
      gift_value: currentTier ? formatMoney(currentTier.value) : '',
      next_threshold: state.nextTier ? formatMoney(state.nextTier.threshold) : '',
      cart_total: formatMoney(state.nonGiftTotal),
      tier_count: String(state.unlockedCount),
    };
  }

  /* ========================================================================
     Toast
     ======================================================================== */

  var toastTimeout = null;

  function showToast(type, tierOrItem) {
    if (!config.ui.toast.enabled) return;

    var el = document.getElementById('bt-gwp-toast');
    if (!el) return;

    var template =
      type === 'add' ? config.ui.toast.addMsg : config.ui.toast.removeMsg;

    // Build token data for this specific toast
    var tierName = '';
    var remaining = 0;

    if (tierOrItem) {
      // Could be a tier object (for add) or cart item (for remove)
      if (tierOrItem.name) {
        tierName = tierOrItem.name;
      } else if (tierOrItem.properties && tierOrItem.properties._gift_tier) {
        // It's a cart item, look up the tier
        var tierId = tierOrItem.properties._gift_tier;
        for (var i = 0; i < config.tiers.length; i++) {
          if (config.tiers[i].id === tierId) {
            tierName = config.tiers[i].name;
            remaining = config.tiers[i].threshold - _lastNonGiftTotal;
            if (remaining < 0) remaining = 0;
            break;
          }
        }
      }
    }

    var data = {
      gift_name: tierName,
      remaining: formatMoney(remaining),
    };

    el.textContent = replaceTokens(template, data);

    // Show toast
    el.classList.add('bt-gwp-toast--visible');

    // Clear any existing timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }

    toastTimeout = setTimeout(function () {
      el.classList.remove('bt-gwp-toast--visible');
      toastTimeout = null;
    }, config.ui.toast.duration * 1000);
  }

  /* ========================================================================
     CSS Custom Properties
     ======================================================================== */

  function hexToRgb(hex) {
    if (!hex) return { r: 0, g: 0, b: 0 };
    hex = hex.replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    var num = parseInt(hex, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    };
  }

  function applyCSSProperties() {
    var root = document.documentElement;
    var ui = config.ui;

    // Bar properties
    root.style.setProperty('--bt-gwp-bar-bg', ui.bar.bgColor);
    root.style.setProperty('--bt-gwp-bar-fill', ui.bar.fillColor);
    root.style.setProperty('--bt-gwp-bar-fill-end', ui.bar.fillEndColor);
    root.style.setProperty('--bt-gwp-bar-text-color', ui.bar.textColor);
    root.style.setProperty('--bt-gwp-bar-accent', ui.bar.accentColor);
    root.style.setProperty('--bt-gwp-bar-radius', ui.bar.borderRadius + 'px');
    root.style.setProperty('--bt-gwp-bar-height', ui.bar.height + 'px');

    // Gift row properties
    var rgb = hexToRgb(ui.gift.rowBg);
    var opacity = ui.gift.rowOpacity / 100;
    root.style.setProperty(
      '--bt-gwp-gift-row-bg',
      'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + opacity + ')'
    );
    root.style.setProperty(
      '--bt-gwp-gift-row-border-color',
      'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', 0.3)'
    );

    // Badge properties
    root.style.setProperty('--bt-gwp-badge-bg', ui.gift.badgeBg);
    root.style.setProperty('--bt-gwp-badge-color', ui.gift.badgeColor);

    // Toast properties
    root.style.setProperty('--bt-gwp-toast-bg', ui.toast.bg);
    root.style.setProperty('--bt-gwp-toast-color', ui.toast.color);

    // Announcement bar properties
    root.style.setProperty('--bt-gwp-announce-bg', ui.announce.bg);
    root.style.setProperty('--bt-gwp-announce-color', ui.announce.color);
    root.style.setProperty('--bt-gwp-announce-font-size', ui.announce.fontSize + 'px');
    root.style.setProperty('--bt-gwp-announce-padding', ui.announce.padding + 'px');
  }

  /* ========================================================================
     UI Updates (event-driven)
     ======================================================================== */

  function updateProgressBars(detail) {
    var bars = document.querySelectorAll('.bt-gwp-progress');
    if (bars.length === 0) return;

    var tokenData = buildTokenData(detail);

    // Determine which message template to use
    var messageTemplate;
    if (detail.allUnlocked) {
      messageTemplate = config.ui.messages.all;
    } else if (detail.unlockedCount > 0) {
      messageTemplate = config.ui.messages.partial;
    } else {
      messageTemplate = config.ui.messages.none;
    }

    var messageText = replaceTokens(messageTemplate, tokenData);

    // Calculate remaining text
    var remainingText = '';
    if (config.ui.bar.showRemaining && detail.nextTier) {
      var remaining = detail.nextTier.threshold - detail.nonGiftTotal;
      if (remaining > 0) {
        remainingText = 'Rs ' + formatMoney(remaining) + ' to go';
      }
    }

    bars.forEach(function (bar) {
      // Update fill width
      var fill = bar.querySelector('.bt-gwp-progress__fill');
      if (fill) {
        fill.style.width = detail.progressPercent + '%';
      }

      // Update message text
      var msgEl = bar.querySelector('.bt-gwp-progress__message-text');
      if (msgEl) {
        msgEl.textContent = messageText;
      }

      // Update remaining text
      var remEl = bar.querySelector('.bt-gwp-progress__remaining');
      if (remEl) {
        remEl.textContent = remainingText;
      }

      // Update marker reached states
      var markers = bar.querySelectorAll('.bt-gwp-progress__marker');
      markers.forEach(function (marker) {
        var tierId = marker.dataset.tierId;
        var reached = false;
        for (var i = 0; i < detail.qualifyingTiers.length; i++) {
          if (detail.qualifyingTiers[i].id === tierId) {
            reached = true;
            break;
          }
        }
        marker.classList.toggle('bt-gwp-progress__marker--reached', reached);
      });

      // Update label reached states
      var labels = bar.querySelectorAll('.bt-gwp-progress__label');
      labels.forEach(function (label) {
        var tierId = label.dataset.tierId;
        var reached = false;
        for (var i = 0; i < detail.qualifyingTiers.length; i++) {
          if (detail.qualifyingTiers[i].id === tierId) {
            reached = true;
            break;
          }
        }
        label.classList.toggle('bt-gwp-progress__label--reached', reached);

        // Show/hide checkmark
        var checkEl = label.querySelector('.bt-gwp-progress__label-check');
        if (checkEl) {
          checkEl.style.display = reached ? '' : 'none';
        }
      });
    });
  }

  function updateAnnouncementBar(detail) {
    var announceEl = document.getElementById('bt-gwp-announce');
    if (!announceEl) return;

    var tokenData = buildTokenData(detail);

    // Determine which message template to use
    var messageTemplate;
    if (detail.allUnlocked) {
      messageTemplate = config.ui.announce.msgAll;
    } else if (detail.unlockedCount > 0) {
      messageTemplate = config.ui.announce.msgPartial;
    } else {
      messageTemplate = config.ui.announce.msgNone;
    }

    var messageText = replaceTokens(messageTemplate, tokenData);

    var msgSpan = announceEl.querySelector('.bt-gwp-announce__message');
    if (msgSpan) {
      msgSpan.textContent = messageText;
    }
  }

  /* ========================================================================
     Cart Sync
     ======================================================================== */

  var isSyncing = false;
  var pendingSync = false;
  var _lastNonGiftTotal = 0;
  var lastSyncTime = 0;

  async function syncCart() {
    if (isSyncing) {
      pendingSync = true;
      return;
    }
    isSyncing = true;

    try {
      var cart = await fetchCart();
      var state = getGiftState(cart);
      _lastNonGiftTotal = state.nonGiftTotal;
      var changed = false;

      // 1. Remove unqualified gifts (sequential, using item.key)
      for (var r = 0; r < state.toRemove.length; r++) {
        var removeItem = state.toRemove[r];
        try {
          await removeFromCart(removeItem.key);
          changed = true;
          showToast('remove', removeItem);
        } catch (e) {
          console.error('[GWP] Failed to remove gift:', e);
        }
      }

      // 2. Fix wrong quantities (sequential)
      for (var f = 0; f < state.toFix.length; f++) {
        var fixItem = state.toFix[f];
        try {
          await updateCartItem(fixItem.key, 1);
          changed = true;
        } catch (e) {
          console.error('[GWP] Failed to fix gift quantity:', e);
        }
      }

      // 3. Add missing gifts (sequential, try/catch for out-of-stock)
      for (var a = 0; a < state.toAdd.length; a++) {
        var addTier = state.toAdd[a];
        try {
          await addToCart(addTier.variantId, addTier.id);
          changed = true;
          showToast('add', addTier);
        } catch (e) {
          // Out of stock or other error — skip this tier silently
          console.warn('[GWP] Failed to add gift (may be out of stock):', addTier.id, e);
        }
      }

      // 4. Re-fetch cart if we made changes and recompute state
      if (changed) {
        cart = await fetchCart();
        state = getGiftState(cart);
      }

      // 5. Dispatch bt:gwp:updated CustomEvent for UI updates
      document.dispatchEvent(
        new CustomEvent('bt:gwp:updated', {
          detail: state,
        })
      );

      // 6. Publish cartUpdate for Dawn UI refresh (if cart changed)
      if (changed && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'gwp-engine',
          cartData: cart,
        });
      }
    } catch (e) {
      console.error('[GWP] syncCart error:', e);
    } finally {
      lastSyncTime = Date.now();
      isSyncing = false;
      if (pendingSync) {
        pendingSync = false;
        debouncedSync();
      }
    }
  }

  // 300ms debounce wrapper
  var debounceTimer = null;
  function debouncedSync() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      syncCart();
    }, 300);
  }

  /* ========================================================================
     Announcement Bar Dismiss
     ======================================================================== */

  function initAnnounceDismiss() {
    var announceEl = document.getElementById('bt-gwp-announce');
    if (!announceEl) return;

    // In "existing" position mode, hide Dawn's built-in announcement bar
    // so the GWP bar takes its slot (instead of stacking two bars).
    if (announceEl.getAttribute('data-bt-gwp-position') === 'existing') {
      var dawnBar = document.querySelector('.announcement-bar-section');
      if (dawnBar) {
        dawnBar.style.display = 'none';
      }
    }

    // Check if previously dismissed this session
    if (sessionStorage.getItem('bt-gwp-announce-dismissed') === 'true') {
      announceEl.classList.add('bt-gwp-announce--hidden');
      return;
    }

    var closeBtn = announceEl.querySelector('.bt-gwp-announce__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        announceEl.classList.add('bt-gwp-announce--hidden');
        sessionStorage.setItem('bt-gwp-announce-dismissed', 'true');
      });
    }
  }

  /* ========================================================================
     Init
     ======================================================================== */

  var config = null;

  document.addEventListener('DOMContentLoaded', function () {
    // Load config
    config = loadConfig();
    if (!config) return;

    // Apply CSS custom properties from theme settings
    applyCSSProperties();

    // Init announcement bar dismiss
    initAnnounceDismiss();

    // Listen to bt:gwp:updated for UI updates
    document.addEventListener('bt:gwp:updated', function (event) {
      updateProgressBars(event.detail);
      updateAnnouncementBar(event.detail);
    });

    // Subscribe to Dawn's cartUpdate pub/sub
    // CRITICAL: skip when source is 'gwp-engine' to prevent infinite loop
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'gwp-engine') {
          return;
        }
        if (Date.now() - lastSyncTime < 1000) {
          return;
        }
        debouncedSync();
      });
    }

    // Run initial sync
    syncCart();
  });
})();
